import { applyThemes, cssVariable } from '../../theme/theme';

/**
 * Thème sombre des studios (ADR-146 §3.3, addendum (10)) : le mode `dark` du thème de l'application, resserré
 * pour le contraste là où le mode sombre de référence garde des jetons pensés pour le clair (texte atténué,
 * accent, bordures, états). Les valeurs sont des jetons, posés en variables sur le chrome du studio seulement :
 * le canevas et les boîtes de dialogue gardent les jetons clairs de l'application. Chaque paire texte / fond
 * utilisée par le studio est vérifiée à 4,5:1 au moins (test de contraste).
 */
export const STUDIO_DARK_TOKENS: Readonly<Record<string, string>> = {
  'color.surface.canvas': '#1C2620',
  'color.surface.default': '#26302B',
  'color.surface.muted': '#1C2620',
  'color.surface.sunken': '#161D19',
  'color.text.primary': '#FFFFFF',
  'color.text.secondary': '#D4DCD7',
  'color.text.muted': '#B4C0B9',
  'color.border.default': 'rgba(255, 255, 255, 0.16)',
  'color.border.strong': 'rgba(255, 255, 255, 0.32)',
  'color.accent.default': '#9CD3AE',
  'color.focus.ring': '#8AB4FF',
  'color.status.success': '#9CD3AE',
  'color.status.success_soft': '#1F3A2A',
  'color.status.warning': '#F2C38B',
  'color.status.warning_soft': '#3A3022',
  'color.status.danger': '#F5A898',
  'color.status.danger_soft': '#3E2622',
  'color.action.primary': '#2F5D3A',
  'color.action.on_primary': '#FFFFFF',
};

/** Paires texte / fond du chrome du studio : chacune doit atteindre 4,5:1 (texte normal, WCAG 2.1 AA). */
export const STUDIO_CONTRAST_PAIRS: ReadonlyArray<readonly [text: string, background: string, where: string]> = [
  ['color.text.primary', 'color.surface.default', 'barre, panneaux, inspecteur'],
  ['color.text.secondary', 'color.surface.default', 'textes secondaires'],
  ['color.text.muted', 'color.surface.default', 'libellés de format, onglets non choisis, aides'],
  ['color.text.muted', 'color.surface.canvas', 'champs de saisie du studio'],
  ['color.text.primary', 'color.surface.canvas', 'champs de saisie du studio'],
  ['color.surface.default', 'color.accent.default', 'choix de groupe d’une condition'],
  ['color.status.danger', 'color.status.danger_soft', 'refus de la recette'],
  ['color.status.warning', 'color.status.warning_soft', 'avertissements'],
  ['color.status.success', 'color.status.success_soft', 'vérifié'],
  ['color.action.on_primary', 'color.action.primary', 'bouton « Proposer… »'],
];

/** Variables CSS du chrome sombre : le mode `dark`, puis les jetons resserrés ci-dessus. */
export function studioChrome(): Record<string, string> {
  const variables = { ...applyThemes([], 'dark').variables };
  for (const [path, value] of Object.entries(STUDIO_DARK_TOKENS)) variables[cssVariable(path)] = value;
  return variables;
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Couleur `#RRGGBB` ou `rgba(r, g, b, a)` posée sur un fond opaque, en composantes 0-255. */
function rgb(color: string, under: readonly [number, number, number] = [0, 0, 0]): [number, number, number] {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (hex !== null) return [parseInt(hex[1]!, 16), parseInt(hex[2]!, 16), parseInt(hex[3]!, 16)];
  const parts = /^rgba?\(([^)]+)\)$/.exec(color)?.[1]?.split(',').map((part) => Number(part.trim()));
  if (parts === undefined || parts.length < 3) throw new Error(`Couleur illisible : ${color}`);
  const alpha = parts[3] ?? 1;
  return [0, 1, 2].map((index) => parts[index]! * alpha + under[index]! * (1 - alpha)) as [number, number, number];
}

/** Rapport de contraste WCAG entre deux couleurs (la seconde est le fond). */
export function contrastRatio(text: string, background: string): number {
  const back = rgb(background);
  const luminance = ([r, g, b]: readonly [number, number, number]): number => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  const a = luminance(rgb(text, back));
  const b = luminance(back);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Tailles de texte des studios (addendum (10)) : la police du design system (Inter, `typography.family`), en
 * quatre tailles ; rien sous 12 px pour un libellé, 13-14 px pour le texte courant.
 */
export const STUDIO_TYPE: Readonly<Record<'sm' | 'md' | 'lg' | 'xl', number>> = { sm: 12, md: 13, lg: 14, xl: 16 };

/** Dimensions de la coquille, reprises de la maquette FormulaireCanevas (px). */
export const STUDIO_LAYOUT = { bar: 52, rail: 52, panel: 270, inspector: 320 } as const;

/** Variables de taille de texte posées sur la racine du studio. */
export function studioTypeVariables(): Record<string, string> {
  return Object.fromEntries(Object.entries(STUDIO_TYPE).map(([size, px]) => [`--ay-studio-text-${size}`, `${px}px`]));
}
